import useTerms from "../../../hooks/useTerms";
import React, { useEffect, useState } from "react";
import { Box, MenuItem } from "@mui/material";
import { PaymentsOutlined as PaymentIcon } from "@mui/icons-material";
import { DrawerField } from "../../molecules";
import { ChoiceChipGroup, FormDatePicker, FormSelect } from "../../atoms";
import { useClinicTheme } from "../../../theme";
import { formatMoney, kurusToInput, parseMoneyInput, PAYMENT_METHOD_LABELS } from "../../../utils/money";
import { DrawerInput, FormDrawer, dateTr, todayYmd } from "./billingParts";

const METHOD_OPTIONS = Object.entries(PAYMENT_METHOD_LABELS).map(([value, label]) => ({ value, label }));

/**
 * "Ödeme al" drawer'ı. Tutar TL girilir (virgüllü), kuruş gönderilir.
 * İsteğe bağlı olarak bir paket satışına bağlanır (yalnız bilgi amaçlı).
 *
 * @param {number} [suggestedAmount] - kalan borç (kuruş); varsa tutar önerisi
 * @param {Array} [packages] - hastanın paketleri (bağlamak için)
 */
export default function PaymentDrawer({ open, onClose, onSave, saving, suggestedAmount, packages = [] }) {
  const terms = useTerms();
  const { palette } = useClinicTheme();
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("cash");
  const [paidAt, setPaidAt] = useState(todayYmd());
  const [packageId, setPackageId] = useState("");
  const [note, setNote] = useState("");

  useEffect(() => {
    if (!open) return;
    setAmount(suggestedAmount > 0 ? kurusToInput(suggestedAmount) : "");
    setMethod("cash");
    setPaidAt(todayYmd());
    setPackageId("");
    setNote("");
  }, [open, suggestedAmount]);

  const kurus = parseMoneyInput(amount);
  const valid = kurus !== null && kurus > 0 && method;
  const isToday = paidAt === todayYmd();

  const submit = () =>
    valid &&
    onSave({
      amount: kurus,
      method,
      // Bugünse şimdiki saat; geçmiş günse öğlen (saat dilimi kaymasın)
      paidAt: isToday ? new Date().toISOString() : new Date(`${paidAt}T12:00:00`).toISOString(),
      patientPackageId: packageId || undefined,
      note: note.trim() || undefined,
    });

  const sellable = packages.filter((p) => p.status !== "cancelled");

  return (
    <FormDrawer
      open={open}
      onClose={onClose}
      icon={<PaymentIcon />}
      title="Ödeme al"
      subtitle={`${terms.patientGen} hesabına ödeme kaydı. Fatura yerine geçmez.`}
      onSubmit={submit}
      submitLabel={valid ? `Kaydet · ${formatMoney(kurus)}` : "Kaydet"}
      loading={saving}
      disabled={!valid}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
        <DrawerField
          label="Tutar (₺) *"
          hint={
            amount && (kurus === null || kurus <= 0)
              ? "Geçerli bir tutar girin (ör. 1.250,00)."
              : suggestedAmount > 0
                ? `Kalan borç ${formatMoney(suggestedAmount)}`
                : undefined
          }
        >
          <DrawerInput
            palette={palette}
            autoFocus
            placeholder="0,00"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            inputProps={{ inputMode: "decimal", "aria-label": "Tutar" }}
          />
        </DrawerField>
        <DrawerField label="Ödeme yöntemi *">
          <ChoiceChipGroup
            ariaLabel="Ödeme yöntemi"
            value={method}
            onChange={(v) => v && setMethod(v)}
            options={METHOD_OPTIONS}
            size="medium"
            sx={{ mt: 0.75 }}
          />
        </DrawerField>
        <DrawerField label="Ödeme tarihi">
          <FormDatePicker
            variant="standard"
            palette={palette}
            fullWidth
            value={paidAt}
            maxDate={todayYmd()}
            onChange={(v) => setPaidAt(v || todayYmd())}
          />
        </DrawerField>
        {sellable.length ? (
          <DrawerField label="Paket" hint="İsteğe bağlı: ödemenin hangi paket için alındığı.">
            <FormSelect
              palette={palette}
              variant="standard"
              value={packageId}
              onChange={setPackageId}
              placeholder={null}
              SelectProps={{ displayEmpty: true }}
            >
              <MenuItem value="">Pakete bağlama</MenuItem>
              {sellable.map((p) => (
                <MenuItem key={p._id} value={p._id}>
                  {p.template?.name} · {dateTr(p.startedAt)} · {formatMoney(p.template?.price)}
                </MenuItem>
              ))}
            </FormSelect>
          </DrawerField>
        ) : null}
        <DrawerField label="Not">
          <DrawerInput
            palette={palette}
            multiline
            minRows={2}
            placeholder="İsteğe bağlı (ör. dekont no)"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            inputProps={{ maxLength: 500, "aria-label": "Not" }}
          />
        </DrawerField>
      </Box>
    </FormDrawer>
  );
}
